import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import servicetypeapi from "Services/Servicetype";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ServiceUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState([]);

  // Max file size
  const maxFileSize = 5242880;
  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than 5 MB`);
      return {
        code: "file-too-large",
        message: `File size is larger than 5 MB`,
      };
    }
    return null;
  };

  // Dropzone for serviceimage props
  const { getRootProps: rootforserviceimage, getInputProps: inputforserviceimage } = useDropzone({
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("serviceImagefile", file);
          formik.setFieldValue("serviceImage", base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  // Dropzone for descriptionimage props
  const { getRootProps: rootfordescimage, getInputProps: inputfordescimage } = useDropzone({
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("descriptionImagefile", file);
          formik.setFieldValue("descriptionImage", base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  // Fetch Service Details
  const getDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    servicetypeapi.getServicebyid(id).then(onSuccess, onFailure);
  }, [id, dispatch]);

  useEffect(() => {
    openDialog && id && getDetails();
  }, [openDialog, id, getDetails]);

  const handleUpdateServicetype = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = {
      serviceName: formValues?.serviceName,
      serviceImage: formValues?.serviceImagefile,
      serviceDescritpion: formValues?.description,
      description_image: formValues?.descriptionImagefile,
      seatsAvailable: formValues?.seatsAvailable,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "Service Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to update data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    servicetypeapi.updateService(id, { ...data }).then(onSuccess, onFailure);
  };

  // Formik - Set fetched values for master
  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("serviceName", details?.serviceName || "");
      formik.setFieldValue("serviceImage", details?.serviceImage || "");
      formik.setFieldValue("serviceImagefile", details?.serviceImage || "");
      formik.setFieldValue("description", details?.serviceDescritpion || "");
      formik.setFieldValue("descriptionImage", details?.description_image || "");
      formik.setFieldValue("descriptionImagefile", details?.description_image || "");
      formik.setFieldValue("seatsAvailable", details?.seatsAvailable || "");
    }
  }, [details]);

  // Formik
  const formik = useFormik({
    initialValues: {
      serviceImage: "",
      serviceImagefile: "",
      serviceName: "",
      description: "",
      descriptionImage: "",
      descriptionImagefile: "",
      seatsAvailable: "",
    },
    validationSchema: yup.object({
      serviceName: yup.string().required("Service Name is required").nullable(),
      description: yup.string().required("Description is required").nullable(),
      seatsAvailable: yup.string().required("Seats Available is required").nullable(),
      descriptionImage: yup.string().required("Description Image is required").nullable(),
      serviceImage: yup.string().required("Service Image is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdateServicetype,
  });

  const handleModalClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      maxWidth="md"
      className={classes.root}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>Update Services</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeButton}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Service Image */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.uploadLabel}>SERVICE IMAGE</Typography>
            <Box {...rootforserviceimage()} className={classes.uploadBox}>
              <input {...inputforserviceimage()} />
              {formik?.values?.serviceImage &&
              formik?.values?.serviceImage !== "/assets/images/logos/no-image.png" ? (
                <img
                  src={formik?.values?.serviceImage}
                  alt="Service Preview"
                  className={classes.previewImg}
                />
              ) : (
                <FileUploadOutlinedIcon className={classes.uploadArrowIcon} />
              )}
              <Box className={classes.updateIconPill}>
                <CameraAltOutlinedIcon className={classes.cameraIcon} />
                <Typography className={classes.updateIconText}>Update Icon</Typography>
              </Box>
            </Box>
            {formik.errors.serviceImage && formik.touched.serviceImage && (
              <Typography className={classes.errorMessage}>
                {formik.errors.serviceImage}
              </Typography>
            )}
          </Grid>

          {/* Description Image */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.uploadLabel}>DESCRIPTION IMAGE</Typography>
            <Box {...rootfordescimage()} className={classes.uploadBox}>
              <input {...inputfordescimage()} />
              {formik?.values?.descriptionImage &&
              formik?.values?.descriptionImage !== "/assets/images/logos/no-image.png" ? (
                <img
                  src={formik?.values?.descriptionImage}
                  alt="Description Preview"
                  className={classes.previewImg}
                />
              ) : (
                <FileUploadOutlinedIcon className={classes.uploadArrowIcon} />
              )}
              <Box className={classes.updateIconPill}>
                <CameraAltOutlinedIcon className={classes.cameraIcon} />
                <Typography className={classes.updateIconText}>Update Icon</Typography>
              </Box>
            </Box>
            {formik.errors.descriptionImage && formik.touched.descriptionImage && (
              <Typography className={classes.errorMessage}>
                {formik.errors.descriptionImage}
              </Typography>
            )}
          </Grid>

          {/* Service Name */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.inputFieldLabel}>Service Name</Typography>
            <TextField
              name="serviceName"
              id="serviceName"
              placeholder="Sedan"
              value={formik?.values?.serviceName}
              onChange={formik.handleChange}
              error={formik?.errors?.serviceName && formik?.touched?.serviceName}
              helperText={
                formik?.errors?.serviceName &&
                formik?.touched?.serviceName &&
                formik?.errors?.serviceName
              }
              fullWidth
              variant="outlined"
              size="small"
              className={classes.customTextField}
            />
          </Grid>

          {/* Seats Available */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.inputFieldLabel}>Seats Available</Typography>
            <TextField
              name="seatsAvailable"
              id="seatsAvailable"
              type="number"
              placeholder="4"
              inputProps={{ min: 0 }}
              value={formik?.values?.seatsAvailable}
              onChange={formik.handleChange}
              error={formik?.errors?.seatsAvailable && formik?.touched?.seatsAvailable}
              helperText={
                formik?.errors?.seatsAvailable &&
                formik?.touched?.seatsAvailable &&
                formik?.errors?.seatsAvailable
              }
              fullWidth
              variant="outlined"
              size="small"
              className={classes.customTextField}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Typography className={classes.inputFieldLabel}>Description</Typography>
            <TextField
              name="description"
              id="description"
              placeholder="Enter the details"
              multiline
              rows={4}
              value={formik?.values?.description}
              onChange={formik.handleChange}
              error={formik?.errors?.description && formik?.touched?.description}
              helperText={
                formik?.errors?.description &&
                formik?.touched?.description &&
                formik?.errors?.description
              }
              fullWidth
              variant="outlined"
              className={classes.customTextField}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Box className={classes.actionButtonsContainer}>
              <Button
                variant="outlined"
                className={classes.closeButtonAction}
                onClick={handleModalClose}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={classes.submitButtonAction}
                onClick={formik?.handleSubmit}
              >
                Update services
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceUpdate;
